import React, { useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";

import GeneralLayout from "components/PageLayouts/GeneralLayout";
import GenericRecordSelection from "components/Records/RecordSelection/GenericRecordSelection";

import AddConversationPopup from "./Conversations/AddConversationsPopup";
import ConversationsRightDrawer from "./Conversations/ConversationsRightDrawer";
import ConversationsBottomDrawer from "./Conversations/ConversationsBottomDrawer";
import ConversationsDetail from "./Conversations/ConversationsDetail";
import ConversationEmptyPage from "./Conversations/ConversationsEmptyPage";

import { conversationsAPI } from "api/Interactions/ConversationsAPI";
import { Conversation } from "api/Interactions/InteractionsInterfaces";

const Conversations: React.FC = () => {
  const [conversationsData, setConversationsData] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [loading, setLoading] = useState(true);

  const { id: routeId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // SAFE activeId (null, never 0)
  const activeId =
    routeId ? Number(routeId) :
    searchParams.get("id") ? Number(searchParams.get("id")) :
    null;

  // Load list of conversations
  useEffect(() => {
    conversationsAPI
      .getAll()
      .then(setConversationsData)
      .finally(() => setLoading(false));
  }, []);

  // Load specific conversation from URL
  useEffect(() => {
    if (!activeId) {
      setSelectedConversation(null);
      return;
    }

    conversationsAPI
      .getById(activeId)
      .then(setSelectedConversation)
      .catch(() => setSelectedConversation(null));
  }, [activeId]);

  // When selecting from sidebar
  const handleSelectConversation = (id: number) => {
    navigate(`/interactions/conversations/${id}`);
  };

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
        <CircularProgress />
      </Box>
    );

  return (
    <GeneralLayout
      useLeftDrawer
      useRightDrawer
      useBottomDrawer
      defaultOpenLeft
      defaultOpenRight={false}
      defaultOpenBottom={false}
      leftDrawerContent={
        <GenericRecordSelection<Conversation>
          items={conversationsData}
          selectedId={activeId}
          onSelect={(c) => handleSelectConversation(c.id)}
          AddPopupComponent={AddConversationPopup}
          onCreated={(newConversation) => {
            setConversationsData((prev) => [...prev, newConversation]);
            navigate(`/interactions/conversations/${newConversation.id}`);
          }}
          idKey="id"
          nameKey={(m) => m.name || `Conversation ${m.id}`}
          colorKey="interactionsPrimary"
          searchPlaceholder="Search conversations..."
        />
      }
      rightDrawerContent={
        activeId ? <ConversationsRightDrawer conversationId={activeId} /> : null
      }
      bottomDrawerContent={
        activeId ? <ConversationsBottomDrawer conversationId={activeId} /> : null
      }
    >
      {selectedConversation ? (
        <ConversationsDetail
          conversationId={activeId!}
          onClose={() => navigate("/interactions/conversations/")}
        />
      ) : (
        <ConversationEmptyPage
          onConversationsCreated={(newConversation) => {
            setConversationsData((prev) => [...prev, newConversation]);
            navigate(`/interactions/conversations/${newConversation.id}`);
          }}
        />
      )}
    </GeneralLayout>
  );
};

export default Conversations;
